import { useLayoutEffect, useRef, useEffect, useState } from "react";

import { UIView } from "@uirouter/react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import styled, { createGlobalStyle } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";

import { setIsUserOnMobileDeviceMobileState } from "app/actions/isUserOnMobileDeviceActions";
import { isHidden, isLocked } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { OnboardingAgent } from "app/components/onboarding-agent/OnboardingAgent/OnboardingAgent";
import {
    syncOnboardingAgentExperimentWithGtm,
    useOnboardingAgentEnabled,
} from "app/components/onboarding-agent/onboardingAgentExperiment";
import QualificationSideBar from "app/components/QualificationSideBar/QualificationSideBar";
import { BranchNameComponent } from "app/components/React/BrancNameBanner/BranchNameComponent";
import BubbleNotificationContainer from "app/components/React/BubbleNotificationContainer/BubbleNotificationContainer";
import EducationBarContainer from "app/components/React/EducationBarContainer/EducationBarContainer";
import ContactUsModalContainer from "app/components/React/Modals/ContactUsModalContainer";
import NotificationBarContainer from "app/components/React/NotificationBarContainer/NotificationBarContainer";
import NoTouchNotAllowedModal from "app/components/React/NoTouchNotAllowedModal/NoTouchNotAllowedModal";
import Toasts from "app/components/React/Toast/toasts-container";
import { AccessDeniedModal } from "app/components/React/UnlockModalProvider/AccessDeniedModal";
import { PrimarySideBar } from "app/components/sidebar3/primary-side-bar/PrimarySideBar";
import { SecondarySideBar } from "app/components/sidebar3/secondary-side-bar/SecondarySideBar";
import { fetchShouldShowModal } from "app/components/UsersAddOnModal/userAddOnFetcher";
import UsersAddOnModal from "app/components/UsersAddOnModal/UsersAddOnModal";
import { ContextLayer } from "app/contexts/ContextLayer/ContextLayer";
import { useCelloInitializer } from "app/custom-hooks/cello/useCelloInitializer";
import { CustomIndustryAgentBanner } from "app/pages/industry-analysis/market-builder/components/customIndustryAgentBanner/CustomIndustryAgentBanner";
import { isCustomIndustryBuilderAvailable } from "app/pages/industry-analysis/market-builder/services/isCustomIndustryBuilderAvailable";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import OptInManager from "app/pages/workspace/common/OptInManager";
import { toastForAddOnBuyers } from "app/routes/handlers/ToastForAddOnBuyers";
import { isBranchNameHidden } from "app/services/dev-toggle/devToggle";
import { getIsMobileDevice } from "app/services/userDevice/getIsMobileDevice";
import { getCueSubscription } from "app/user-data/Solutions";
import { isAnnual } from "app/UtilitiesAndConstants/UtilityFunctions/trialUsersUtils";
import { isNoTouchOrAfterTrial } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const SideBarContainer = styled.div`
    position: absolute;
    height: 100%;
    z-index: 1055;
    display: flex;
`;
const GlobalStyleForSidebar3 = createGlobalStyle`
    .highcharts-tooltip-container {
        z-index: 1056 !important;
    }

    .DurationSelector-popup-container {
        z-index: 1056 !important;
    }

    /* PoP dropdown */
    .DropdownContent-container {
        z-index: 1071 !important;
    }
    
    .swReactTable-header-wrapper.css-sticky-header {
        z-index: 1072 !important;
    }

    .QueryBarAutoComplete {
        z-index: 1054 !important;
    }
`;

const SideBarToRender = ({ isSecondarySidebarVisible }: { isSecondarySidebarVisible: boolean }) => {
    const primarySidebarRef = useRef<{ onMouseLeavePrimarySidebar: VoidFunction } | null>(null);
    const sidebarContainerRef = useRef<HTMLDivElement>(null);

    const hasAiStudio = swSettings.components.Home.resources.HasAiStudio;
    const showAiStudio = hasAiStudio && !proNavigator.isSalesSolution();
    const cueSubscription = proNavigator.isSalesSolution() ? undefined : getCueSubscription();
    const quickSearchClaim = swSettings.components?.QuickSearch;
    const showQuickSearch = !proNavigator.isSalesSolution() && !isHidden(quickSearchClaim);
    const isQuickSearchLocked = isLocked(quickSearchClaim);

    const showSuperAgentButton = proNavigator.isSalesSolution() && salesSettingsHelper.isSuperAgentEnabled;

    const onMouseLeave = () => {
        primarySidebarRef.current?.onMouseLeavePrimarySidebar();
    };

    return (
        <SideBarContainer
            id="SideBarContainer"
            ref={sidebarContainerRef}
            className="hidden-print"
            onMouseLeave={onMouseLeave}
        >
            <GlobalStyleForSidebar3 />
            <PrimarySideBar
                ref={primarySidebarRef}
                sidebarContainerRef={sidebarContainerRef}
                showQuickSearch={showQuickSearch}
                isQuickSearchLocked={isQuickSearchLocked}
                showAiStudio={showAiStudio}
                cueSubscription={cueSubscription}
                showSuperAgentButton={showSuperAgentButton}
            />
            <SecondarySideBar isVisible={isSecondarySidebarVisible} />
        </SideBarContainer>
    );
};

export const SwLayout = () => {
    const dispatch = useDispatch();
    const { isSecondaryBar3Open, secondaryBarType3 } = useSelector((state) => state.secondaryBar);
    const { isUserOnMobileDevice } = useSelector((state) => state.isUserOnMobileDevice);
    const [shouldShowAddOnModal, setShouldShowAddOnModal] = useState(false);
    const isSecondarySidebarVisible = isSecondaryBar3Open && secondaryBarType3 !== "None";
    const sidebarClassNameSuffix = isSecondarySidebarVisible ? "sidebar3-push" : "sidebar3";
    const showOnboardingAgent = useOnboardingAgentEnabled();

    const { getLocale } = useTranslate();
    const locale = getLocale();

    useCelloInitializer({
        locale: locale,
        swSettings,
        isEnabled: isNoTouchOrAfterTrial(swSettings),
    });

    toastForAddOnBuyers();

    useEffect(() => {
        const checkModalStatus = async () => {
            const shouldShow = await fetchShouldShowModal();
            setShouldShowAddOnModal(shouldShow);
        };

        if (isAnnual()) {
            checkModalStatus();
        }
    }, []);

    const branchName = swSettings.version?.split(".")[1];

    useLayoutEffect(() => {
        if (getIsMobileDevice()) {
            dispatch(setIsUserOnMobileDeviceMobileState(true));
        }
    }, []);

    useEffect(() => {
        syncOnboardingAgentExperimentWithGtm();
    }, [showOnboardingAgent]);

    return (
        <>
            <Helmet>
                <meta
                    name="viewport"
                    content={
                        isUserOnMobileDevice ? "width=1280, initial-scale=0.5" : "width=device-width, initial-scale=1.0"
                    }
                />
            </Helmet>
            <ContextLayer>
                <div
                    className="sw-layout sw-layout-no-scroll-container fadeIn"
                    data-automation-pro-load-indicator
                >
                    {shouldShowAddOnModal ? (
                        <UsersAddOnModal
                            isOpen={shouldShowAddOnModal}
                            setIsOpen={setShouldShowAddOnModal}
                        />
                    ) : null}
                    <QualificationSideBar />
                    <OptInManager />
                    <NotificationBarContainer />
                    <BubbleNotificationContainer />
                    <div className="sw-layout-no-scroll-container sw-layout-section">
                        <SideBarToRender isSecondarySidebarVisible={isSecondarySidebarVisible} />
                        <div
                            className={`sw-layout-no-scroll-container sw-layout-section-content fadeIn ${sidebarClassNameSuffix}`}
                        >
                            <UIView />
                        </div>
                    </div>
                    <EducationBarContainer />
                    <Toasts />
                    <NoTouchNotAllowedModal />
                    <AccessDeniedModal />
                    <ContactUsModalContainer />
                    {!isBranchNameHidden() && branchName ? <BranchNameComponent /> : null}
                </div>
                {showOnboardingAgent ? <OnboardingAgent /> : null}
                {/* Gated on the same claim as the builder: mounting it regardless is what
                    subscribes the run service, so an account without the feature would both
                    see the banner and resume polling a run it can no longer reach. */}
                {isCustomIndustryBuilderAvailable() ? <CustomIndustryAgentBanner /> : null}
            </ContextLayer>
        </>
    );
};
